import React, { useState, useEffect } from "react";
import {
  ThinTextField,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { fetchData } from "../../utils/functions";
import { toast } from "react-toastify";
import Select from "react-select";
import * as Yup from "yup";

const BasicSchoolProfile = ({ data, isDqaApproved: propIsDqaApproved }) => {

  const [formData, setFormData] = useState({
    school_name: "",
    semis_code: "",
    village: "",
    school_level: "",
    school_type: "",
    school_category: "",
    medium: "",
    shifts: "",
    enrollment_boys: "",
    enrollment_girls: "",
    enrollment_total: "",
    head_teacher: "",
    contact_no: "",
    date_of_collection: dayjs(),
    collected_by: "",
    extracurricular: [],
    mission: "",
    status_of_school: "",
    latitude: "",
    longitude: "",
    uc_name: "",
  });

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);
  const [missionOptions, setMissionOptions] = useState([]);
  const [initialVillageLoaded, setInitialVillageLoaded] = useState(false);

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluka, setSelectedTaluka] = useState("");
  const [selectedDeh, setSelectedDeh] = useState("");
  const [selectedDehName, setSelectedDehName] = useState("");
  const [selectedVillage, setSelectedVillage] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState({});
    // const [isDqaRejected, setIsDqaRejected] = useState(false);

  // Use prop value or fallback to checking data
  const isDqaApproved =
    propIsDqaApproved ||
    (data && (data.verified_from_dqa === 1 || data.verified_from_dqa === "1"));

    const isDqaRejected =
      data && (data.verified_from_dqa === 2 || data.verified_from_dqa === "2");

  const schoolProfileSchema = Yup.object().shape({
    district_id: Yup.string().required("District is required"),
    tehsil_id: Yup.string().required("Taluka is required"),
    deh_id: Yup.string().required("Deh is required"),
    village_id: Yup.string().required("Village is required"),
    school_name: Yup.string().required("School Name is required"),
    semis_code: Yup.string()
      .matches(/^\d{9}$/, "SEMIS Code must be exactly 9 digits")
      .required("SEMIS Code is required"),
    school_level: Yup.string().required("School Level is required"),
    school_type: Yup.string().required("School Type is required"),
    school_category: Yup.string().required("School Category is required"),
    latitude: Yup.number()
      .typeError("Latitude must be a valid number")
      .required("Latitude is required"),
    longitude: Yup.number()
      .typeError("Longitude must be a valid number")
      .required("Longitude is required"),
    status_of_school: Yup.string().required("Status of School is required"),
    mission: Yup.string().nullable().required("Mission is required"),
    uc_name: Yup.string().nullable(),
    medium: Yup.string().when("status_of_school", {
      is: "Functional",
      then: (schema) => schema.required("Medium of Instruction is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    shifts: Yup.string().when("status_of_school", {
      is: "Functional",
      then: (schema) => schema.required("Number of Shifts is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    enrollment_boys: Yup.mixed().when("status_of_school", {
      is: "Functional",
      then: () =>
        Yup.number()
          .typeError("Boys enrollment must be a number")
          .min(0, "Value cannot be negative")
          .required("Enrollment (Boys) is required"),
      otherwise: () => Yup.mixed().nullable(),
    }),

    enrollment_girls: Yup.mixed().when("status_of_school", {
      is: "Functional",
      then: () =>
        Yup.number()
          .typeError("Girls enrollment must be a number")
          .min(0, "Value cannot be negative")
          .required("Enrollment (Girls) is required"),
      otherwise: () => Yup.mixed().nullable(),
    }),

    head_teacher: Yup.string().when("status_of_school", {
      is: "Functional",
      then: (schema) => schema.required("Head Teacher name is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    contact_no: Yup.mixed().when("status_of_school", {
      is: "Functional",
      then: () =>
        Yup.string()
          .matches(
            /^03\d{2}-?\d{7}$/,
            "Contact must start with 03 and be 11 digits"
          )
          .required("Contact number is required"),
      otherwise: () => Yup.mixed().nullable(),
    }),

    date_of_collection: Yup.date().required(
      "Date of Data Collection is required"
    ),
  });

  // Function to load villages for a specific deh_id
  const loadVillagesForDeh = async (dehId, villageIdToSelect = null) => {
    if (!dehId) {
      setVillages([]);
      return;
    }

    try {
      const res = await fetchData(`villages?deh_id=${dehId}`);
      const loadedVillages = res?.data || [];

      setVillages(loadedVillages);

      // If we have a villageIdToSelect, set it after villages are loaded
      if (villageIdToSelect && loadedVillages.length > 0) {
        const villageExists = loadedVillages.some(
          (v) => v.village_id?.toString() === villageIdToSelect.toString()
        );

        if (villageExists) {
          setSelectedVillage(villageIdToSelect.toString());
        } else {
          console.warn(
            `Village ${villageIdToSelect} not found in loaded villages`
          );

          // Still set the selection
          setSelectedVillage(villageIdToSelect.toString());

          // Add a dummy option for the missing village
          if (villageIdToSelect) {
            const missingVillage = {
              village_id: villageIdToSelect,
              village_name: `[Village ID: ${villageIdToSelect}] - Not found in current deh`,
              deh_id: dehId,
            };
            setVillages((prev) => [...prev, missingVillage]);
          }
        }
        setInitialVillageLoaded(true);
      }
    } catch (error) {
      console.error("Failed to fetch villages:", error);
      toast.error("Failed to fetch villages");
      setVillages([]);
    }
  };

  // Initialize form with data
  useEffect(() => {
    if (data && !initialVillageLoaded) {

      // Set district, taluka, deh first
      const districtId = String(data.district_id || "");
      const talukaId = String(data.tehsil_id || "");
      const dehId = String(data.deh_id || "");
      const villageId = String(data.village_id || "");

      setSelectedDistrict(districtId);
      setSelectedTaluka(talukaId);
      setSelectedDeh(dehId);
      setSelectedVillage(villageId);

      // Set form data
      setFormData({
        school_name: data.school_name || "",
        semis_code: data.semis_code || "",
        village: data.address || "",
        school_level: data.school_level || "",
        status_of_school: data.status_of_school || "",
        latitude: data.latitude ? Number(data.latitude).toFixed(7) : "",
        longitude: data.longitude ? Number(data.longitude).toFixed(7) : "",
        school_type: data.school_type || "",
        school_category: data.school_category || "",
        medium: data.medium || "",
        shifts: data.number_of_shifts || "",
        enrollment_boys: String(data.enrollment_of_boys ?? ""),
        enrollment_girls: String(data.enrollment_of_girls ?? ""),
        enrollment_total: String(data.enrollment_total ?? ""),
        head_teacher: data.head_teacher_name || "",
        contact_no: data.head_teacher_contact || "",
        date_of_collection: data.date_of_data_collection
          ? dayjs(data.date_of_data_collection)
          : dayjs(),
        collected_by: data.createdBy_name || "",
        mission: data.mission || "", // This will be set
        uc_name: data.uc_name || "",
      });

      // Load villages for the deh if we have it
      if (dehId) {
        loadVillagesForDeh(dehId, villageId);
      } else {
        setInitialVillageLoaded(true);
      }
    }
  }, [data, initialVillageLoaded]);

  // Load districts on mount
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const res = await fetchData("districts");
        setDistricts(res?.data || []);
      } catch {
        // toast.error("Failed to fetch districts");
      }
    };
    loadDistricts();
  }, []);

  // Load tehsils when district changes
  useEffect(() => {
    if (!selectedDistrict) {
      setTehsils([]);
      return;
    }

    const loadTehsils = async () => {
      try {
        const res = await fetchData(`tehsils?district_id=${selectedDistrict}`);
        const tehsilsData = res?.data || [];
        setTehsils(tehsilsData);

        // If we have data from props and tehsil is already selected, ensure it's in the list
        if (data?.tehsil_id && selectedTaluka === String(data.tehsil_id)) {
          const tehsilExists = tehsilsData.some(
            (t) => t.tehsil_id.toString() === String(data.tehsil_id)
          );
          if (!tehsilExists) {
            // Reset if tehsil not found
            setSelectedTaluka("");
          }
        }
      } catch {
        // toast.error("Failed to fetch tehsils");
      }
    };

    loadTehsils();
  }, [selectedDistrict]);

  // Load dehs when taluka changes
  useEffect(() => {
    if (!selectedTaluka) {
      setDehs([]);
      setVillages([]);
      return;
    }

    const loadDehs = async () => {
      try {
        const res = await fetchData(`dehs?tehsil_id=${selectedTaluka}`);
        const dehsData = res?.data || [];
        setDehs(dehsData);

        // Find and set deh name
        if (selectedDeh) {
          const selectedDehObj = dehsData.find(
            (d) => String(d.deh_id) === selectedDeh
          );
          if (selectedDehObj) {
            setSelectedDehName(selectedDehObj?.deh_name || "");
          }
        }

        // If we have data from props and deh is already selected, ensure it's in the list
        if (data?.deh_id && selectedDeh === String(data.deh_id)) {
          const dehExists = dehsData.some(
            (d) => d.deh_id.toString() === String(data.deh_id)
          );
          if (!dehExists) {
            // Reset if deh not found
            setSelectedDeh("");
            setSelectedDehName("");
            setVillages([]);
          }
        }
      } catch {
        // toast.error("Failed to fetch dehs");
      }
    };

    loadDehs();
  }, [selectedTaluka]);

  // Load villages when deh changes (for normal user interaction)
  useEffect(() => {
    if (selectedDeh) {
      // Only load villages if we're changing deh (not during initial load)
      if (
        villages.length === 0 ||
        !villages.some((v) => v.deh_id?.toString() === selectedDeh)
      ) {
        loadVillagesForDeh(selectedDeh);
      }
    } else {
      setVillages([]);
    }
  }, [selectedDeh]);

  // Update deh name when deh is selected
  useEffect(() => {
    if (selectedDeh && dehs.length > 0) {
      const d = dehs.find((d) => String(d.deh_id) === selectedDeh);
      if (d) {
        setSelectedDehName(d?.deh_name || "");
      }
    }
  }, [selectedDeh, dehs]);

  // Calculate total enrollment
  useEffect(() => {
    const boys = parseInt(formData.enrollment_boys) || 0;
    const girls = parseInt(formData.enrollment_girls) || 0;
    const total = boys + girls;
    setFormData((prev) => ({
      ...prev,
      enrollment_total: total > 0 ? total.toString() : "",
    }));
  }, [formData.enrollment_boys, formData.enrollment_girls]);

  // Update mission options when deh name changes
  useEffect(() => {
    if (selectedDehName) {
      const missions = Array.from({ length: 25 }, (_, i) => ({
        value: `${selectedDehName}_M${String(i + 1).padStart(2, "0")}`,
        label: `${selectedDehName}_M${String(i + 1).padStart(2, "0")}`,
      }));

      // Add "complete" option for mobile compatibility
      missions.push({ value: "complete", label: "Complete" });
      setMissionOptions(missions);
    }
  }, [selectedDehName]);

  // Set mission value when mission options are loaded
  useEffect(() => {
    if (data?.mission && missionOptions.length > 0) {
      // Check if current mission exists in options
      const missionExists = missionOptions.some(
        (option) => option.value === data.mission
      );

      if (!missionExists) {
        // If mission from data doesn't exist in options, add it
        const updatedOptions = [
          ...missionOptions,
          { value: data.mission, label: data.mission },
        ];
        setMissionOptions(updatedOptions);
      }
    }
  }, [data?.mission, missionOptions]);

  const schoolLevels = [
    { label: "Primary", value: "Primary" },
    { label: "Middle", value: "Middle" },
    { label: "Secondary", value: "Secondary" },
    { label: "Higher Secondary", value: "Higher Secondary" },
  ];

  const school_status = [
    { label: "Functional", value: "Functional" },
    { label: "Non-Functional", value: "Non-Functional" },
  ];

  const school_type = [
    { label: "Government", value: "Government" },
    { label: "Private", value: "Private" },
    { label: "Community-Based", value: "Community-Based" },
    { label: "NGO Based", value: "NGO Based" },
  ];

  const school_category = [
    { label: "Boys", value: "Boys" },
    { label: "Girls", value: "Girls" },
    { label: "Mixed", value: "Mixed" },
  ];

  const mediums = [
    { label: "Sindhi", value: "Sindhi" },
    { label: "Urdu", value: "Urdu" },
    { label: "English", value: "English" },
  ];

  const shifts = [
    { label: "Single", value: "Single" },
    { label: "Double", value: "Double" },
    { label: "Triple", value: "Triple" },
  ];

  const districtOptions = districts.map((d) => ({
    value: String(d.district_id),
    label: d.district_name,
  }));

  const talukaOptions = tehsils
    .filter(
      (t) => !selectedDistrict || t.district_id.toString() === selectedDistrict
    )
    .map((t) => ({ value: String(t.tehsil_id), label: t.tehsil_name }));

  const dehOptions = dehs
    .filter((d) => !selectedTaluka || d.tehsil_id.toString() === selectedTaluka)
    .map((d) => ({ value: String(d.deh_id), label: d.deh_name }));

  // Create village options - Always include selected village
  const villageOptions = (() => {
    const baseOptions = villages.map((v) => ({
      value: String(v.village_id),
      label: v.village_name,
      className: v.village_name?.includes("[Village ID:")
        ? "text-red-500 italic"
        : "",
    }));

    // Check if selectedVillage exists in villages
    const selectedVillageExists = villages.some(
      (v) => v.village_id?.toString() === selectedVillage?.toString()
    );

    // If we have a selected village that's not in the list, add it
    if (selectedVillage && !selectedVillageExists) {
      const missingOption = {
        value: selectedVillage,
        label: `[Village ID: ${selectedVillage}] - Selected (Not in current Deh)`,
        className: "text-red-500 italic",
      };
      return [missingOption, ...baseOptions];
    }

    return baseOptions;
  })();

  const handleNumericChange = (field, value) => {
    if (isDqaApproved) return; // Prevent changes if DQA approved

    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      clearFieldError(field);
    }
  };

  const clearFieldError = (field) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleChange = (field, value) => {
    if (isDqaApproved) return; // Prevent changes if DQA approved

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    clearFieldError(field);
  };

  const handleLocationChange = (setter, field, value) => {
    if (isDqaApproved) return; // Prevent changes if DQA approved

    setter(value);
    if (field === "district_id") {
      setSelectedTaluka("");
      setSelectedDeh("");
      setSelectedDehName("");
      setSelectedVillage("");
      setVillages([]);
      setFormData((prev) => ({ ...prev, mission: "" }));
    } else if (field === "tehsil_id") {
      setSelectedDeh("");
      setSelectedDehName("");
      setSelectedVillage("");
      setVillages([]);
      setFormData((prev) => ({ ...prev, mission: "" }));
    } else if (field === "deh_id") {
      const selectedDehObj = dehs.find((d) => String(d.deh_id) === value);
      setSelectedDehName(selectedDehObj?.deh_name || "");
      setSelectedVillage("");
      setVillages([]);
      setFormData((prev) => ({ ...prev, mission: "" }));
    } else if (field === "village_id") {
      setSelectedVillage(value);
    }

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleUpdate = async () => {
    // Prevent update if DQA approved
    if (isDqaApproved) {
      toast.error(
        "This assessment has been approved by DQA and cannot be modified."
      );
      return;
    }

    setIsUpdating(true);
    setErrors({});

    try {
      const currentFormData = { ...formData };
      const currentLocations = {
        district_id: selectedDistrict,
        tehsil_id: selectedTaluka,
        deh_id: selectedDeh,
        village_id: selectedVillage,
      };

      const validationData = {
        ...currentFormData,
        ...currentLocations,
      };

      await schoolProfileSchema.validate(validationData, { abortEarly: false });

      const duplicateCheck = await fetchData(
        "school-check-semis",
        "GET",
        null,
        {},
        {
          semis_code: currentFormData.semis_code,
          school_profile_id: data.school_profile_id,
        }
      );

      if (
        duplicateCheck?.data?.exists &&
        duplicateCheck?.data?.school_profile_id !== data.school_profile_id
      ) {
        setErrors((prev) => ({
          ...prev,
          semis_code:
            "This SEMIS Code already exists. Please enter a unique one.",
        }));
        toast.error("SEMIS Code already exists.");
        setIsUpdating(false);
        return;
      }

      const updatePayload = {
        semis_code: currentFormData.semis_code,
        village_id: selectedVillage,
        district_id: selectedDistrict,
        tehsil_id: selectedTaluka,
        deh_id: selectedDeh,
        school_name: currentFormData.school_name,
        address: currentFormData.village,
        school_level: currentFormData.school_level,
        school_type: currentFormData.school_type,
        school_category: currentFormData.school_category,
        medium: currentFormData.medium || null,
        number_of_shifts: currentFormData.shifts || null,
        enrollment_of_boys: parseInt(currentFormData.enrollment_boys) || 0,
        enrollment_of_girls: parseInt(currentFormData.enrollment_girls) || 0,
        enrollment_total: parseInt(currentFormData.enrollment_total) || 0,
        head_teacher_name: currentFormData.head_teacher,
        head_teacher_contact: currentFormData.contact_no,
        date_of_data_collection:
          currentFormData.date_of_collection.format("YYYY-MM-DD"),
        status_of_school: currentFormData.status_of_school,
        latitude: parseFloat(currentFormData.latitude) || null,
        longitude: parseFloat(currentFormData.longitude) || null,
        mission: currentFormData.mission,
        uc_name: currentFormData.uc_name,
      };

      const response = await fetchData(
        `school-combined-update/${data.school_profile_id}`,
        "PUT",
        { school_profile: JSON.stringify(updatePayload) },
        { "Content-Type": "application/json" }
      );

      if (response.success) {
        toast.success("School profile updated successfully!");
      } else {
        toast.error(response.message || "Failed to update school profile");
      }
    } catch (error) {
      console.error("Update error:", error);
      if (error.inner) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        toast.error("An error occurred while updating the school profile");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {/* DQA Approved Banner */}
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              School Information
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              School Information
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <ThinDropdownWithSearch
            label="District "
            options={districtOptions}
            value={selectedDistrict}
            onChange={(value) =>
              handleLocationChange(setSelectedDistrict, "district_id", value)
            }
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.district_id && (
            <p className="text-red-500 text-xs">{errors.district_id}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Taluka "
            options={talukaOptions}
            value={selectedTaluka}
            onChange={(value) =>
              handleLocationChange(setSelectedTaluka, "tehsil_id", value)
            }
            disabled={!selectedDistrict || isDqaApproved || isDqaRejected}
          />
          {errors.tehsil_id && (
            <p className="text-red-500 text-xs">{errors.tehsil_id}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="Union Council"
            value={formData.uc_name || ""}
            onChange={(e) => handleChange("uc_name", e.target.value)}
            placeholder="Enter UC"
            isRequired={false}
            disabled={isDqaApproved || isDqaRejected}
          />
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Deh "
            options={dehOptions}
            value={selectedDeh}
            onChange={(value) =>
              handleLocationChange(setSelectedDeh, "deh_id", value)
            }
            disabled={!selectedTaluka || isDqaApproved || isDqaRejected}
          />
          {errors.deh_id && (
            <p className="text-red-500 text-xs">{errors.deh_id}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Mission"
            options={missionOptions}
            value={formData.mission}
            onChange={(val) => handleChange("mission", val)}
            disabled={!selectedDeh || isDqaApproved || isDqaRejected}
          />
          {errors.mission && (
            <p className="text-red-500 text-xs">{errors.mission}</p>
          )}
          {!selectedDeh && !isDqaApproved && (
            <p className="text-gray-500 text-xs mt-1">
              Please select a Deh first
            </p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Village "
            options={villageOptions}
            value={selectedVillage}
            onChange={(value) =>
              handleLocationChange(setSelectedVillage, "village_id", value)
            }
            disabled={!selectedDeh || isDqaApproved || isDqaRejected}
          />
          {errors.village_id && (
            <p className="text-red-500 text-xs">{errors.village_id}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="SEMIS Code"
            value={formData.semis_code}
            onChange={(e) => {
              if (isDqaApproved) return; // Prevent changes if DQA approved
              const value = e.target.value;
              if (/^\d{0,9}$/.test(value)) {
                handleChange("semis_code", value);
              }
            }}
            inputProps={{ maxLength: 9 }}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.semis_code && (
            <p className="text-red-500 text-xs mt-1">{errors.semis_code}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="School Name "
            value={formData.school_name}
            onChange={(e) => handleChange("school_name", e.target.value)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.school_name && (
            <p className="text-red-500 text-xs">{errors.school_name}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="School Address "
            value={formData.village}
            isRequired={false}
            onChange={(e) => handleChange("village", e.target.value)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.village && (
            <p className="text-red-500 text-xs">{errors.village}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="School Level "
            options={schoolLevels}
            value={formData.school_level}
            onChange={(val) => handleChange("school_level", val)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.school_level && (
            <p className="text-red-500 text-xs">{errors.school_level}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="School Category "
            options={school_category}
            value={formData.school_category}
            onChange={(val) => handleChange("school_category", val)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.school_category && (
            <p className="text-red-500 text-xs">{errors.school_category}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="School Type "
            options={school_type}
            value={formData.school_type}
            onChange={(val) => handleChange("school_type", val)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.school_type && (
            <p className="text-red-500 text-xs">{errors.school_type}</p>
          )}
        </div>
        <div>
          <ThinDropdownWithSearch
            label="Status of School "
            options={school_status}
            value={formData.status_of_school}
            onChange={(val) => handleChange("status_of_school", val)}
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.status_of_school && (
            <p className="text-red-500 text-xs">{errors.status_of_school}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="Latitude "
            type="number"
            value={formData.latitude}
            onChange={(e) => handleChange("latitude", e.target.value)}
            disabled={true} // Already disabled
          />
          {errors.latitude && (
            <p className="text-red-500 text-xs">{errors.latitude}</p>
          )}
        </div>
        <div>
          <ThinTextField
            label="Longitude "
            type="number"
            value={formData.longitude}
            onChange={(e) => handleChange("longitude", e.target.value)}
            disabled={true} // Already disabled
          />
          {errors.longitude && (
            <p className="text-red-500 text-xs">{errors.longitude}</p>
          )}
        </div>
        {formData.status_of_school === "Functional" && (
          <>
            <div>
              <ThinDropdownWithSearch
                label="Medium of Instruction "
                options={mediums}
                value={formData.medium}
                onChange={(val) => handleChange("medium", val)}
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.medium && (
                <p className="text-red-500 text-xs">{errors.medium}</p>
              )}
            </div>
            <div>
              <ThinDropdownWithSearch
                label="Number of Shifts "
                options={shifts}
                value={formData.shifts}
                onChange={(val) => handleChange("shifts", val)}
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.shifts && (
                <p className="text-red-500 text-xs">{errors.shifts}</p>
              )}
            </div>
            <div>
              <ThinTextField
                label="Enrollment (Boys) "
                type="number"
                value={formData.enrollment_boys}
                onChange={(e) =>
                  handleNumericChange("enrollment_boys", e.target.value)
                }
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.enrollment_boys && (
                <p className="text-red-500 text-xs">{errors.enrollment_boys}</p>
              )}
            </div>
            <div>
              <ThinTextField
                label="Enrollment (Girls) "
                type="number"
                value={formData.enrollment_girls}
                onChange={(e) =>
                  handleNumericChange("enrollment_girls", e.target.value)
                }
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.enrollment_girls && (
                <p className="text-red-500 text-xs">
                  {errors.enrollment_girls}
                </p>
              )}
            </div>
            <div>
              <ThinTextField
                label="Enrollment (Total)"
                type="number"
                value={formData.enrollment_total}
                disabled
                style={{ backgroundColor: "#f3f4f6" }}
              />
            </div>
            <div>
              <ThinTextField
                label="Head Teacher "
                value={formData.head_teacher}
                onChange={(e) => handleChange("head_teacher", e.target.value)}
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.head_teacher && (
                <p className="text-red-500 text-xs">{errors.head_teacher}</p>
              )}
            </div>
            <div>
              <ThinTextField
                label="Contact No. "
                value={formData.contact_no}
                onChange={(e) => {
                  if (isDqaApproved) return; // Prevent changes if DQA approved
                  let input = e.target.value.replace(/\D/g, "");
                  if (input.length > 4) {
                    input = input.slice(0, 4) + "-" + input.slice(4, 11);
                  }
                  setFormData((prev) => ({ ...prev, contact_no: input }));
                }}
                inputProps={{ maxLength: 12 }}
                disabled={isDqaApproved || isDqaRejected}
              />
              {errors.contact_no && (
                <p className="text-red-500 text-xs">{errors.contact_no}</p>
              )}
            </div>
          </>
        )}
        <div>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of Data Collection "
              value={formData.date_of_collection}
              onChange={(newVal) => handleChange("date_of_collection", newVal)}
              slotProps={{ textField: { fullWidth: true } }}
              format="DD/MM/YYYY"
              disabled={true} // Already disabled
            />
          </LocalizationProvider>
          {errors.date_of_collection && (
            <p className="text-red-500 text-xs">{errors.date_of_collection}</p>
          )}
        </div>
      </div>

      {/* Conditionally render update button - Only show when NOT DQA approved */}
      {!isDqaApproved && !isDqaRejected ? (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`${
              isUpdating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      ) : (
        <div className=""></div>
      )}
    </>
  );
};

export default BasicSchoolProfile;
